import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Button, Chip, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LeaveEncashmentService } from '../../services/leaveEncashment.service';
import { UserService } from '../../services/user.service';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { displayUsername } from '../../utils/userUtils';
import { ICreateLeaveEncashmentRequest, ILeaveEncashment } from '../../interfaces/payroll.interface';
import LeaveEncashmentFormDialog from './LeaveEncashmentFormDialog';
import SearchableSelect from '../common/SearchableSelect';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Leave encashment lifecycle (spec §2.1/§3): list, create, delete-for-unapplied-only.
// Fix round 1 (F2): the badge is honest per spec §3 ("Applied/Pending badges off
// appliedAt+monthGenerated") and needs THREE states, not two — Applied (appliedAt set), Pending
// (not applied, month not yet generated — a future scheduled run can still pay it), and "Pending
// — month generated" (not applied, but payroll for the target month already ran without it —
// the common cause is §2.2's SKIPPED_INSUFFICIENT_BALANCE or the exit-exclusion path; only an
// explicit re-run of that month, not the next scheduled run, will pay it). Delete visibility
// stays keyed off `appliedAt` ALONE (spec §2.1/H4 — encashments are per-employee, not
// per-institution the way festival grants are, so a monthGenerated-but-unapplied encashment is
// still deletable) — do not conflate the badge's third state with delete eligibility.
const LeaveEncashmentList: React.FC = () => {
    const [encashments, setEncashments] = useState<ILeaveEncashment[]>([]);
    const [usernames, setUsernames] = useState<Record<string, string>>({});
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [data, users] = await Promise.all([
                LeaveEncashmentService.list(institutionId, year),
                UserService.getInstitutionUsers(0, 1000, institutionId).catch(() => ({ content: [] })),
            ]);
            setEncashments(data);
            const map: Record<string, string> = {};
            (users.content || []).forEach((u: { id: string; username: string }) => {
                map[u.id] = displayUsername(u.username);
            });
            setUsernames(map);
            setLoadFailed(false);
        } catch (error) {
            showApiError(error, 'Failed to load leave encashments');
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [institutionId, year, showApiError]);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreate = async (data: ICreateLeaveEncashmentRequest) => {
        setCreating(true);
        try {
            const created = await LeaveEncashmentService.create(data, institutionId);
            showSuccess('Leave encashment created');
            setFormOpen(false);
            if (created.targetYear !== year) {
                setYear(created.targetYear);
            } else {
                load();
            }
        } catch (error) {
            // Surface the backend's rule-naming 400 message verbatim (spec §2.1) rather than
            // predicting it client-side — the dialog stays open so the admin can adjust and retry.
            showApiError(error, 'Failed to create leave encashment');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (encashment: ILeaveEncashment) => {
        try {
            await LeaveEncashmentService.delete(encashment.id, institutionId);
            showSuccess('Leave encashment deleted');
            load();
        } catch (error) {
            showApiError(error, 'Failed to delete leave encashment');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.leaveEncashment.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }} data-help-section="payroll.leaveEncashment.toolbar">
                <Typography variant="h5">Leave Encashment</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isSystemAdmin && (
                    <SearchableSelect
                        label="Select Institution"
                        data-help="payroll.leaveEncashment.institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => setInstitutionId(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ minWidth: 220 }}
                    />
                )}
                <FormControl size="small" sx={{ minWidth: 120 }} data-help="payroll.leaveEncashment.year">
                    <InputLabel id="leave-encashment-year-label">Year</InputLabel>
                    <Select
                        labelId="leave-encashment-year-label"
                        label="Year"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {[year - 1, year, year + 1].map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                <Button data-help="payroll.leaveEncashment.new"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
                    disabled={!institutionId || loadFailed}
                >
                    New Encashment
                </Button>
                
            </Box>

            <TableContainer component={Paper} data-help-section="payroll.leaveEncashment.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.leaveEncashment.col.employee">Employee</TableCell>
                            <TableCell data-help="payroll.leaveEncashment.col.leaveType">Leave Type</TableCell>
                            <TableCell align="right" data-help="payroll.leaveEncashment.col.days">Days</TableCell>
                            <TableCell data-help="payroll.leaveEncashment.col.targetMonth">Target Month</TableCell>
                            <TableCell align="right" data-help="payroll.leaveEncashment.col.amountPaid">Amount Paid</TableCell>
                            <TableCell data-help="payroll.leaveEncashment.col.status">Status</TableCell>
                            <TableCell align="right" data-help="payroll.leaveEncashment.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">Loading...</TableCell>
                            </TableRow>
                        )}
                        {!loading && loadFailed && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Failed to load leave encashments.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && !institutionId && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">Select an institution to view its leave encashments</TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && institutionId && encashments.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No leave encashments found</TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && encashments.map((enc) => {
                            const applied = !!enc.appliedAt;
                            // F2: month already generated without this encashment being applied —
                            // amber "Pending" would promise a payment the next scheduled run will
                            // not make.
                            const staleNotApplied = !applied && enc.monthGenerated;
                            return (
                                <TableRow key={enc.id}>
                                    <TableCell>{usernames[enc.userId] || enc.userId}</TableCell>
                                    <TableCell>{enc.leaveTypeNameNormalized}</TableCell>
                                    <TableCell align="right">{enc.days}</TableCell>
                                    <TableCell>{MONTHS[enc.targetMonth - 1]} {enc.targetYear}</TableCell>
                                    <TableCell align="right">
                                        {enc.amountPaid != null ? Number(enc.amountPaid).toFixed(2) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {applied && <Chip size="small" label="Applied" color="success" />}
                                        {!applied && staleNotApplied && (
                                            <Chip
                                                size="small"
                                                label="Pending — month generated"
                                                color="default"
                                                title="Payroll for this month has already been generated without this encashment. Re-run that month to pay it."
                                            />
                                        )}
                                        {!applied && !staleNotApplied && (
                                            <Chip size="small" label="Pending" color="warning" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {!applied && (
                                            <Tooltip title="Delete encashment">
                                                <IconButton
                                                    size="small"
                                                    data-help="payroll.leaveEncashment.action.delete"
                                                    aria-label={`Delete encashment for ${usernames[enc.userId] || enc.userId}`}
                                                    onClick={() => handleDelete(enc)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <LeaveEncashmentFormDialog
                open={formOpen}
                onClose={() => !creating && setFormOpen(false)}
                onSave={handleCreate}
                saving={creating}
                institutionId={institutionId}
            />
        </Box>
    );
};

export default LeaveEncashmentList;
